'use client';

import { useEffect, useRef, useState } from 'react';
import { Briefcase, Calendar, MapPin, ChevronRight } from 'lucide-react';
import SectionHeader from './SectionHeader';

const EXPERIENCES = [
  {
    id: 1,
    role: 'Full Stack Web Developer',
    company: 'Freelance',
    type: 'Self-Employed',
    period: 'Jan 2023 – Present',
    duration: '2+ years',
    location: 'Remote',
    status: 'current',
    color: '#c9a87c',
    responsibilities: [
      'Designed and developed full-stack web applications for clients across various industries',
      'Built RESTful APIs with Node.js/Express and integrated with React.js frontends',
      'Implemented responsive UI/UX designs using Tailwind CSS and modern CSS frameworks',
      'Deployed applications on cloud platforms and managed CI/CD pipelines',
      'Collaborated with clients to gather requirements and deliver solutions on schedule',
    ],
    tech: ['React.js', 'Node.js', 'Express', 'MongoDB', 'Tailwind CSS', 'Docker'],
  },
  {
    id: 2,
    role: 'Backend Developer (Django)',
    company: 'Academic & Freelance Projects',
    type: 'Project-Based',
    period: 'Sep 2022 – Dec 2023',
    duration: '1+ year',
    location: 'Cairo, Egypt',
    status: 'completed',
    color: '#7d9b8a',
    responsibilities: [
      'Built RESTful APIs using Django REST Framework for web and mobile clients',
      'Designed and managed relational databases with PostgreSQL and MySQL',
      'Implemented JWT-based authentication and authorization systems',
      'Developed admin dashboards with Django admin customization',
      'Wrote unit tests and maintained code quality standards',
    ],
    tech: ['Python', 'Django', 'DRF', 'PostgreSQL', 'JWT', 'Redis'],
  },
];

export default function ExperienceSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeExp, setActiveExp] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleExp = (id: number) => {
    setActiveExp((prev) => (prev === id ? null : id));
  };

  return (
    <section id="experience" ref={sectionRef} className="py-28 xl:py-40 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div
          className={`transition-all duration-800 ease-organic ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader index="03 — Experience" title="My" highlight="journey" />
        </div>

        <div className="space-y-6 max-w-4xl">
          {EXPERIENCES.map((exp, i) => {
            const isOpen = activeExp === exp.id;
            const isOffset = i % 2 === 1;

            return (
              <article
                key={exp.id}
                className={`transition-all duration-700 ease-organic ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } ${isOffset ? 'lg:ml-16 xl:ml-24' : ''}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div
                  className={`warm-card rounded-2xl p-6 xl:p-8 cursor-pointer transition-all duration-400 ${
                    isOpen ? 'border-sand/25 shadow-warm-hover' : 'warm-card-hover'
                  }`}
                  onClick={() => toggleExp(exp.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExp(exp.id);
                    }
                  }}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-start justify-between gap-6 mb-5">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {exp.status === 'current' && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border border-sage/30 bg-sage/10 text-sage">
                            <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
                            Current
                          </span>
                        )}
                        <span className="font-mono text-[10px] text-parchment-muted uppercase tracking-wider">
                          {exp.type}
                        </span>
                      </div>
                      <h3 className="font-display text-xl text-parchment font-700 mb-1">{exp.role}</h3>
                      <p className="text-sand font-500">{exp.company}</p>
                    </div>
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border"
                      style={{
                        backgroundColor: `${exp.color}10`,
                        borderColor: `${exp.color}25`,
                      }}
                    >
                      <Briefcase size={16} style={{ color: exp.color }} />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-parchment-muted text-xs mb-4">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={12} />
                      <span className="font-mono">{exp.period}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin size={12} />
                      {exp.location}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded-md font-mono text-[10px] border"
                      style={{
                        color: exp.color,
                        borderColor: `${exp.color}30`,
                        backgroundColor: `${exp.color}08`,
                      }}
                    >
                      {exp.duration}
                    </span>
                  </div>

                  <div
                    className={`overflow-hidden transition-all duration-500 ease-organic ${
                      isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <ul className="space-y-2.5 mb-5 pt-2">
                      {exp.responsibilities.map((resp, j) => (
                        <li key={j} className="flex items-start gap-2 text-parchment-muted text-sm leading-relaxed">
                          <ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-sand" />
                          {resp}
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-parchment/6">
                      {exp.tech.map((t) => (
                        <span
                          key={t}
                          className="px-2.5 py-1 rounded-md text-xs font-mono border"
                          style={{
                            color: exp.color,
                            borderColor: `${exp.color}25`,
                            backgroundColor: `${exp.color}08`,
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-parchment/6">
                    <ChevronRight
                      size={14}
                      className={`text-parchment-muted transition-transform duration-300 ${
                        isOpen ? 'rotate-90' : ''
                      }`}
                    />
                    <span className="text-parchment-muted text-xs">
                      {isOpen ? 'Show less' : 'Read more'}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
